import React, { useState, useEffect } from 'react';
import { Target } from 'lucide-react';
import challenges from '../data/dailyChallenges.json';
import { supabase } from '../utils/supabase';
import { getClientId } from '../utils/clientId';
import ShareButton from './ShareButton';
import { useAuth } from '../hooks/useAuth';
import { trackChallengeComplete, trackContentView } from '../utils/analytics';

const DailyChallenge = () => {
  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [completionCount, setCompletionCount] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get today's date
  const today = new Date();
  const month = today.getMonth() + 1;
  const day = today.getDate();

  // Find today's challenge
  const todaysChallenge = challenges.challenges.find(
    challenge => challenge.day === day && challenge.month === month
  ) || {
    challenge: "Try to lick your elbow while reciting the alphabet backwards",
    difficulty: "Impossible",
    category: "Physical"
  };

  const fetchCompletionCount = async () => {
    try {
      const todayDate = today.toISOString().split('T')[0];
      const { count, error: countError } = await supabase
        .from('challenge_completions')
        .select('id', { count: 'exact', head: true })
        .eq('challenge', todaysChallenge.challenge)
        .eq('completed_date', todayDate);

      if (countError) {
        console.error('Error getting count:', countError);
        return;
      }
      
      setCompletionCount(count || 0);
    } catch (error) {
      console.error('Error fetching completion count:', error);
    }
  };

  useEffect(() => {
    const checkCompletionStatus = async () => {
      try {
        const clientId = getClientId();
        const todayDate = today.toISOString().split('T')[0];
        
        // Check if user has completed today's challenge
        const { data: completionData, error: completionError } = await supabase
          .from('challenge_completions')
          .select('id')
          .eq('challenge', todaysChallenge.challenge)
          .eq('client_id', clientId)
          .eq('completed_date', todayDate)
          .maybeSingle();

        if (completionError) {
          console.error('Error checking completion:', completionError);
          throw completionError;
        }
        
        setHasCompleted(!!completionData);

        // Get total completion count
        await fetchCompletionCount();
      } catch (error) {
        console.error('Error checking completion status:', error);
        setError('Error checking completion status');
      }
    };

    checkCompletionStatus();
    const interval = setInterval(checkCompletionStatus, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [todaysChallenge.challenge, today]);

  useEffect(() => {
    if (todaysChallenge) {
      trackContentView('Daily Challenge', todaysChallenge.challenge);
    }
  }, [todaysChallenge]);

  const handleCompletion = async () => {
    if (isLoading || hasCompleted) return;

    try {
      setIsLoading(true);
      setError(null);
      const clientId = getClientId();
      const userId = session?.user?.id;

      const { data, error } = await supabase.rpc(
        'rpc_handle_challenge_completion',
        {
          p_challenge: todaysChallenge.challenge,
          p_client_id: clientId,
          p_user_id: userId || null
        }
      );

      if (error) {
        if (error.message.includes('Already completed this challenge today')) {
          setHasCompleted(true);
          setError('You have already completed this challenge today');
        } else {
          console.error('Challenge completion error:', error);
          setError(error.message);
        }
        return;
      }

      setHasCompleted(true);
      await fetchCompletionCount();
      trackChallengeComplete(todaysChallenge.challenge);

      window.dispatchEvent(new CustomEvent('challengeCompleted', {
        detail: {
          points: 20,
          stats: data
        }
      }));
    } catch (error) {
      console.error('Error completing challenge:', error);
      setError(error instanceof Error ? error.message : 'Error completing challenge');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card hover-lift p-6 group">
      <div className="flex items-center space-x-2 mb-6">
        <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 group-hover:from-green-500/30 group-hover:to-emerald-500/30 transition-colors">
          <Target className="h-6 w-6 text-green-400" />
        </div>
        <h3 className="text-xl font-semibold gradient-text">Today's Useless Challenge</h3>
      </div>
      
      <div className="bg-gradient-to-br from-green-500/5 to-emerald-500/5 rounded-lg p-4 mb-6">
        <p className="text-gray-300 leading-relaxed">{todaysChallenge.challenge}</p>
      </div>

      <div className="flex justify-between items-center text-sm mb-6">
        <div className="text-gray-300">
          <span className="text-green-400 font-medium">Difficulty:</span> {todaysChallenge.difficulty}
        </div>
        <div className="text-gray-300">
          <span className="text-green-400 font-medium">Category:</span> {todaysChallenge.category}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <ShareButton 
          title="Can you complete today's useless challenge?"
          text={`Today's Challenge: ${todaysChallenge.challenge} (Difficulty: ${todaysChallenge.difficulty})`}
        />
        <button 
          onClick={handleCompletion}
          disabled={isLoading || hasCompleted}
          className={`gradient-bg text-white py-2 px-4 rounded-lg font-medium transition ${
            isLoading || hasCompleted
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:opacity-90'
          }`}
        >
          {isLoading ? 'Processing...' : hasCompleted ? 'Completed!' : 'I Did It!'}
        </button>
      </div>

      <div className="text-sm text-gray-400 text-center">
        <span className="font-bold text-green-400">{completionCount}</span> {completionCount === 1 ? 'person has' : 'people have'} completed this challenge
      </div>
    </div>
  );
};

export default DailyChallenge;